import { z } from 'zod';
import { ADDRESS_POOL_PROTOCOLS, ALL_YIELD_PROTOCOLS, PRICE_YIELD_PROTOCOLS, } from '../constants/yields.js';
import { addressSchema, isoTimestampSchema, nonEmptyStringSchema, numericLikeSchema, evmChainSlugSchema, } from './common.js';
/** Use to check which protocols use address-like pool ids. */
const addressPoolProtocolSet = new Set(ADDRESS_POOL_PROTOCOLS);
/** Use when a field should allow any supported yield protocol. */
export const yieldProtocolSchema = z.enum(ALL_YIELD_PROTOCOLS);
/** Use when only price-enabled protocols should be allowed. */
export const priceYieldProtocolSchema = z.enum(PRICE_YIELD_PROTOCOLS);
/** Use to filter best-yield results by type. */
export const yieldTypeSchema = z.enum(['lending', 'vault', 'staking']).optional();
/** Use for yield endpoints that filter by chain, token, or protocol. */
export const poolFilterParamsSchema = z.object({
    chain: nonEmptyStringSchema.optional(),
    protocol: yieldProtocolSchema.optional(),
    pool: nonEmptyStringSchema.optional(),
    blockHeight: z.number().int().nonnegative().optional(),
}).strict().superRefine(({ protocol, pool }, context) => {
    if (pool && protocol && addressPoolProtocolSet.has(protocol) && !addressSchema.safeParse(pool).success) {
        context.addIssue({
            code: 'custom',
            path: ['pool'],
            message: 'pool must be a valid EVM address for address-based protocols',
        });
    }
});
/** Use to request one pool price. */
export const getYieldsPriceParamsSchema = z.object({
    chain: evmChainSlugSchema,
    protocol: priceYieldProtocolSchema,
    pool: nonEmptyStringSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
/** Use to request ranked best-yield results. */
export const getYieldsBestParamsSchema = z.object({
    token: nonEmptyStringSchema.optional(),
    minTvl: numericLikeSchema.optional(),
    limit: numericLikeSchema.optional(),
    type: yieldTypeSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
/** Use to request a wallet's pool balances. */
export const getYieldsBalanceParamsSchema = z.object({
    chain: evmChainSlugSchema,
    protocol: yieldProtocolSchema,
    pool: nonEmptyStringSchema,
    address: addressSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
/** Use to request Pendle implied APY data. */
export const getYieldsPendleImpliedApyParamsSchema = z.object({
    chain: evmChainSlugSchema,
    pool: nonEmptyStringSchema,
    address: addressSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
/** Use to list Morpho vaults. */
export const getMorphoVaultsParamsSchema = z.object({
    chain: evmChainSlugSchema,
}).strict();
/** Use to request detailed Morpho vault analytics. */
export const getMorphoVaultDataParamsSchema = z.object({
    chain: evmChainSlugSchema,
    vaults: z.array(addressSchema).min(1).max(50, 'Maximum 50 vaults per request'),
}).strict();
/** APY fields shared by yield responses. */
export const apyBreakdownSchema = z.object({
    baseApy: z.number(),
    extraApy: z.number(),
    totalApy: z.number(),
}).strict();
/** APY fields used by ranked best-yield results. */
export const bestYieldApySchema = z.object({
    totalApy: z.number(),
    baseApy: z.number().optional(),
    extraApy: z.number().optional(),
}).strict();
/** TVL fields shared by yield responses. */
export const tvlBreakdownSchema = z.object({
    underlying: z.record(z.string(), z.number()),
    totals: z.record(z.string(), z.number()),
}).strict();
/** One pool in a yield list response. */
export const yieldPoolDetailSchema = z.object({
    chain: nonEmptyStringSchema,
    pool: nonEmptyStringSchema.optional(),
    protocol: nonEmptyStringSchema,
    apy: apyBreakdownSchema.optional(),
    tvl: tvlBreakdownSchema.optional(),
    error: nonEmptyStringSchema.optional(),
}).strict();
/** Response shape for yield pool lists. */
export const yieldsDataSchema = z.object({
    results: z.array(yieldPoolDetailSchema),
    updatedAt: isoTimestampSchema,
}).strict();
/** One entry in the best-yields response. */
export const bestYieldEntrySchema = z.object({
    rank: z.number().int().positive(),
    chain: nonEmptyStringSchema,
    protocol: nonEmptyStringSchema,
    pool: nonEmptyStringSchema,
    type: z.enum(['lending', 'vault', 'staking']),
    apy: bestYieldApySchema,
    tvl: z.object({ usd: z.number() }).strict().optional(),
    error: nonEmptyStringSchema.optional(),
}).strict();
/** Response shape for ranked best-yield results. */
export const bestYieldsDataSchema = z.object({
    results: z.array(bestYieldEntrySchema),
    updatedAt: isoTimestampSchema,
}).strict();
/** One entry in a pool price response. */
export const poolPriceEntrySchema = z.object({
    chain: nonEmptyStringSchema,
    protocol: nonEmptyStringSchema,
    pool: nonEmptyStringSchema,
    price: z.object({ USD: z.number() }).strict().optional(),
    error: nonEmptyStringSchema.optional(),
}).strict();
/** Response shape for pool prices. */
export const poolPriceDataSchema = z.object({
    results: z.array(poolPriceEntrySchema),
    updatedAt: isoTimestampSchema,
}).strict();
/** One entry in a pool balance response. */
export const poolBalanceEntrySchema = z.object({
    chain: nonEmptyStringSchema,
    protocol: nonEmptyStringSchema,
    pool: nonEmptyStringSchema,
    address: addressSchema,
    balance: z.record(z.string(), z.number()).optional(),
    error: nonEmptyStringSchema.optional(),
}).strict();
/** Response shape for pool balances. */
export const poolBalanceDataSchema = z.object({
    results: z.array(poolBalanceEntrySchema),
    updatedAt: isoTimestampSchema,
}).strict();
/** One entry in a Pendle implied APY response. */
// All fields are optional because the handler's error paths return partial
// objects (sometimes just { error }, sometimes { chain, pool, address, error }).
// The success path returns user, pool, chain (as a stringified decimal chain id,
// not a slug), address, and the APY math fields.
export const pendleImpliedApyEntrySchema = z.object({
    user: nonEmptyStringSchema.optional(),
    chain: nonEmptyStringSchema.optional(),
    pool: nonEmptyStringSchema.optional(),
    address: addressSchema.optional(),
    effectivePtExchangeRate: z.number().optional(),
    expiryDate: nonEmptyStringSchema.optional(),
    daysToExpiry: z.number().nonnegative().optional(),
    impliedApy: z.number().optional(),
    error: nonEmptyStringSchema.optional(),
}).strict();
/** Response shape for Pendle implied APY data. */
export const pendleImpliedApyDataSchema = z.object({
    results: z.array(pendleImpliedApyEntrySchema),
    updatedAt: isoTimestampSchema,
}).strict();
/** One vault in a Morpho vault list. */
export const morphoVaultSchema = z.object({
    address: addressSchema,
    name: nonEmptyStringSchema,
    symbol: nonEmptyStringSchema,
    chainId: z.number().int().positive(),
    chain: z.object({ id: z.number().int().positive(), network: nonEmptyStringSchema }).strict(),
    version: z.enum(['v1', 'v2']).optional(),
    whitelisted: z.boolean().optional(),
    asset: z.object({
        id: nonEmptyStringSchema,
        address: addressSchema,
        decimals: z.number().int().nonnegative(),
    }).strict().optional(),
}).strict();
/** Response shape for Morpho vault lists. */
export const morphoVaultsDataSchema = z.object({
    chain: evmChainSlugSchema,
    count: z.number().int().nonnegative(),
    vaults: z.array(morphoVaultSchema),
}).strict();
/** One vault in a Morpho vault analytics response. */
const morphoTotalAssetsSchema = z.union([nonEmptyStringSchema, z.number()]);
// The handler spreads upstream Morpho GraphQL state objects directly, which
// may include fields beyond the enumerated APY / TVL breakdowns. Catchall
// tolerates those upstream-side additions without requiring a schema release.
const morphoVaultStateSchema = z.object({
    totalAssets: morphoTotalAssetsSchema.optional(),
    totalAssetsUsd: z.number().optional(),
    totalSupply: nonEmptyStringSchema.optional(),
    fee: z.number().optional(),
    apy: z.number().optional(),
    netApy: z.number().optional(),
    avgApy: z.number().optional(),
    avgNetApy: z.number().optional(),
    dailyApy: z.number().optional(),
    dailyNetApy: z.number().optional(),
    weeklyApy: z.number().optional(),
    weeklyNetApy: z.number().optional(),
    monthlyApy: z.number().optional(),
    monthlyNetApy: z.number().optional(),
    quarterlyApy: z.number().optional(),
    quarterlyNetApy: z.number().optional(),
    yearlyApy: z.number().optional(),
    yearlyNetApy: z.number().optional(),
    allTimeApy: z.number().optional(),
    allTimeNetApy: z.number().optional(),
    netApyWithoutRewards: z.number().optional(),
}).catchall(z.unknown());
export const morphoVaultDataItemSchema = z.object({
    address: addressSchema,
    name: nonEmptyStringSchema,
    symbol: nonEmptyStringSchema,
    chainId: z.number().int().positive(),
    chain: z.object({ id: z.number().int().positive(), network: nonEmptyStringSchema }).strict().optional(),
    state: morphoVaultStateSchema.optional(),
    totalAssets: morphoTotalAssetsSchema,
    totalAssetsUsd: z.number(),
    totalSupply: nonEmptyStringSchema,
    fee: z.number(),
    apy: z.number(),
    netApy: z.number(),
    avgApy: z.number(),
    avgNetApy: z.number(),
    lastUpdated: isoTimestampSchema,
    dailyApy: z.number().optional(),
    dailyNetApy: z.number().optional(),
    weeklyApy: z.number().optional(),
    weeklyNetApy: z.number().optional(),
    monthlyApy: z.number().optional(),
    monthlyNetApy: z.number().optional(),
    quarterlyApy: z.number().optional(),
    quarterlyNetApy: z.number().optional(),
    yearlyApy: z.number().optional(),
    yearlyNetApy: z.number().optional(),
    allTimeApy: z.number().optional(),
    allTimeNetApy: z.number().optional(),
    netApyWithoutRewards: z.number().optional(),
    version: z.enum(['v1', 'v2']).optional(),
    liquidity: nonEmptyStringSchema.optional(),
    liquidityUsd: z.number().optional(),
    idleAssetsUsd: z.number().optional(),
    performanceFee: z.number().optional(),
    managementFee: z.number().optional(),
    maxRate: z.number().optional(),
}).strict();
/** Response shape for Morpho vault analytics. */
export const morphoVaultDataListSchema = z.object({
    chain: evmChainSlugSchema,
    count: z.number().int().nonnegative(),
    vaults: z.array(morphoVaultDataItemSchema),
}).strict();
